import { createContext, useContext, useState, useMemo, useCallback } from "react";
import { storageGet, storageSet } from "./log";

export const translations = {
  fr: {
    nav: {
      home: "Accueil",
      rooms: "Chambres",
      gallery: "Galerie",
      location: "Grand-Popo",
      activities: "Activités",
      contact: "Contact",
      book: "Réserver",
    },
    hero: {
      overline: "Grand-Popo · Bénin",
      title: "L'océan, au pas de la porte.",
      subtitle:
        "Guesthouse Bambou Beach — quatre chambres, une plage, un rythme lent.",
      cta_book: "Réserver en direct",
      cta_rooms: "Voir les chambres",
    },
    intro: {
      overline: "Notre maison",
      title: "Une maison de plage, simple et chaleureuse.",
      body:
        "À Ayi-Guinnou, à deux pas du sable, la Guesthouse Bambou Beach vous accueille comme on reçoit des amis. Quatre chambres soignées, un petit-déjeuner, une terrasse avec vue sur l'océan, et l'essentiel — rien de trop, rien qui manque.",
    },
    host: {
      overline: "Votre hôte",
      title: "Votre hôte, Jacky.",
      body:
        "Jacky est le visage de la Guesthouse Bambou Beach. Employée précieuse et de confiance absolue, elle vous accueille personnellement, organise vos navettes, vous oriente vers les bons coins de plage, de pêche ou de cuisine locale. C'est elle qui répond à vos messages — pas un centre d'appels.",
      signature: "— Jacky",
    },
    features: {
      title: "Ce qui vous attend",
      items: [
        { label: "Accès direct à la plage", desc: "Le sable à quelques pas de votre chambre." },
        { label: "Terrasses vue mer", desc: "Pour le petit-déjeuner ou un livre au coucher du soleil." },
        { label: "Petit-déjeuner inclus", desc: "Simple, bon, préparé chaque matin." },
        { label: "Wi-Fi gratuit", desc: "Dans tout l'établissement." },
        { label: "Parking privé", desc: "Sécurisé, gratuit pour nos hôtes." },
        { label: "Navette aéroport", desc: "Sur demande, depuis Cotonou." },
      ],
    },
    rooms: {
      overline: "Chambres",
      title: "Quatre chambres, quatre atmosphères.",
      subtitle:
        "Chaque chambre a son caractère. Toutes partagent le même confort simple et honnête.",
      from: "À partir de",
      per_night: "/ nuit",
      book_cta: "Demander cette chambre",
      details: "Voir les détails",
      layout_overline: "Plan de la maison",
      layout_title: "Où se trouve chaque chambre.",
      layout_body:
        "La maison compte deux niveaux. À l'étage, les chambres Patio et Ocean ouvrent sur un balcon partagé avec vue mer. Au rez-de-chaussée, les chambres Sosso et Tintin donnent sur le jardin.",
      upstairs: "Étage",
      ground_floor: "Rez-de-chaussée",
    },
    gallery: {
      overline: "Galerie",
      title: "Des images valent mieux qu'une brochure.",
      subtitle: "Grand-Popo, la plage, la maison.",
    },
    location: {
      overline: "Situation",
      title: "Grand-Popo, la côte atlantique du Bénin.",
      body:
        "À l'ouest du Bénin, entre l'embouchure du fleuve Mono et l'océan Atlantique, Grand-Popo est un village de pêcheurs connu pour sa plage interminable, ses cocoteraies, et son histoire. La Guesthouse Bambou Beach est située au quartier Ayi-Guinnou, arrondissement d'Agoué, à un pas de la mer.",
      address_label: "Adresse",
      getting_here: "Comment venir",
      airport:
        "Aéroport international de Cotonou (Cadjèhoun) — environ 1h30-2h de route. Navette sur demande.",
      open_maps: "Ouvrir dans Google Maps",
    },
    booking: {
      overline: "Réservation directe",
      title: "Réservez en direct, sans intermédiaire.",
      subtitle:
        "Envoyez-nous votre demande. Nous confirmons la disponibilité et vous répondons sous 24h, par email ou WhatsApp.",
      first_name: "Prénom",
      last_name: "Nom",
      email: "Email",
      phone: "Téléphone / WhatsApp",
      room: "Chambre",
      check_in: "Arrivée",
      check_out: "Départ",
      guests: "Voyageurs",
      message: "Message (optionnel)",
      message_ph:
        "Horaire d'arrivée, navette, régime alimentaire, enfants...",
      submit: "Envoyer la demande",
      submitting: "Envoi en cours...",
      success_title: "Demande envoyée",
      success_body:
        "Merci. Nous revenons vers vous sous 24h pour confirmer votre séjour. Un WhatsApp accélère toujours les choses.",
      error:
        "Une erreur est survenue. Merci de réessayer ou de nous contacter par WhatsApp.",
      smoobu_note:
        "Bientôt disponible : la disponibilité en temps réel via notre système de réservation Smoobu.",
      smoobu_live_note:
        "Disponibilité en temps réel ci-contre, synchronisée avec Booking.com et Airbnb. Aucun risque de double réservation.",
      select_room: "Choisissez une chambre",
    },
    contact: {
      overline: "Contact",
      title: "Parlons-nous.",
      subtitle:
        "Un WhatsApp, un email, un appel — le canal qui vous va le mieux.",
      whatsapp_cta: "Écrire sur WhatsApp",
      email_cta: "Envoyer un email",
      form_title: "Ou envoyez-nous un message",
      name: "Nom",
      email: "Email",
      message: "Message",
      send: "Envoyer",
      sent: "Message envoyé, merci.",
    },
    footer: {
      tagline: "Maison d'hôtes en bord de mer · Grand-Popo, Bénin",
      explore: "Explorer",
      contact: "Contact",
      book_on: "Réservez également sur",
      rights: "Tous droits réservés",
    },
    promo: {
      text: "Réservez en direct et économisez 15% — sans commission d'intermédiaire.",
      short: "-15% en réservant en direct",
      cta: "En profiter",
      badge_title: "Offre réservation directe",
      badge_body:
        "Réservez directement sur notre site et bénéficiez de 15% de réduction sur le tarif affiché. Aucune commission, un contact humain.",
    },
    activities: {
      overline: "Activités · Circuits",
      title: "Quatre circuits pour Grand-Popo.",
      subtitle:
        "Au-delà du sable, il y a la mangrove, la Bouche du Roy, les villages Vodoun, les campements peulhs. Voici quatre circuits que nous organisons avec les guides locaux.",
      source:
        "Contenu basé sur les brochures officielles de l'Office de Tourisme Grand-Popo / Bénin.",
      duration: "Durée",
      means: "Moyen",
      stops: "Au programme",
      book: "Réserver ce circuit (WhatsApp)",
      cta_overline: "Vous hésitez ?",
      cta_title: "Parlez-en avec Jacky.",
      cta_body:
        "Jacky connaît chaque guide personnellement. Elle vous aide à choisir le circuit qui correspond à votre rythme, votre envie, votre jour.",
      office: "Guides partenaires : Lazare Dossa & Frédéric Kpokoudjo — Office de Tourisme Grand-Popo / Bénin",
    },
    common: {
      required: "Requis",
      invalid_email: "Email invalide",
      loading: "Chargement...",
    },
  },
  en: {
    nav: {
      home: "Home",
      rooms: "Rooms",
      gallery: "Gallery",
      location: "Grand-Popo",
      activities: "Activities",
      contact: "Contact",
      book: "Book",
    },
    hero: {
      overline: "Grand-Popo · Benin",
      title: "The ocean, right at your door.",
      subtitle:
        "Guesthouse Bambou Beach — four rooms, one beach, a slow pace.",
      cta_book: "Book direct",
      cta_rooms: "See the rooms",
    },
    intro: {
      overline: "Our house",
      title: "A simple, warm little beach house.",
      body:
        "In Ayi-Guinnou, steps from the sand, Guesthouse Bambou Beach welcomes you like a friend. Four well-kept rooms, breakfast, a terrace facing the ocean, and the essentials — nothing extra, nothing missing.",
    },
    host: {
      overline: "Your host",
      title: "Your host, Jacky.",
      body:
        "Jacky is the face of Guesthouse Bambou Beach. A valuable and absolutely trustworthy team member, she welcomes you personally, arranges your shuttle, points you to the best beach spots, fishing corners, and local kitchens. She is the one answering your messages — not a call center.",
      signature: "— Jacky",
    },
    features: {
      title: "What awaits you",
      items: [
        { label: "Direct beach access", desc: "Sand just a few steps from your room." },
        { label: "Sea-view terraces", desc: "For breakfast or a book at sunset." },
        { label: "Breakfast included", desc: "Simple, good, made fresh every morning." },
        { label: "Free Wi-Fi", desc: "Throughout the property." },
        { label: "Private parking", desc: "Secure and free for our guests." },
        { label: "Airport shuttle", desc: "On request, from Cotonou." },
      ],
    },
    rooms: {
      overline: "Rooms",
      title: "Four rooms, four atmospheres.",
      subtitle:
        "Each room has its character. All share the same simple, honest comfort.",
      from: "From",
      per_night: "/ night",
      book_cta: "Request this room",
      details: "See details",
      layout_overline: "House layout",
      layout_title: "Where each room sits.",
      layout_body:
        "The house has two floors. Upstairs, Patio and Ocean open onto a shared balcony with a sea view. On the ground floor, Sosso and Tintin face the garden.",
      upstairs: "Upstairs",
      ground_floor: "Ground floor",
    },
    gallery: {
      overline: "Gallery",
      title: "Pictures tell it better than a brochure.",
      subtitle: "Grand-Popo, the beach, the house.",
    },
    location: {
      overline: "Location",
      title: "Grand-Popo, Benin's Atlantic coast.",
      body:
        "In western Benin, between the mouth of the Mono river and the Atlantic Ocean, Grand-Popo is a fishing village known for its endless beach, coconut groves, and history. Guesthouse Bambou Beach sits in the Ayi-Guinnou quarter, Agoué district, a step from the sea.",
      address_label: "Address",
      getting_here: "Getting here",
      airport:
        "Cotonou International Airport (Cadjèhoun) — around 1h30-2h by road. Shuttle on request.",
      open_maps: "Open in Google Maps",
    },
    booking: {
      overline: "Direct booking",
      title: "Book direct, no middleman.",
      subtitle:
        "Send us your request. We confirm availability and get back to you within 24h, by email or WhatsApp.",
      first_name: "First name",
      last_name: "Last name",
      email: "Email",
      phone: "Phone / WhatsApp",
      room: "Room",
      check_in: "Check-in",
      check_out: "Check-out",
      guests: "Guests",
      message: "Message (optional)",
      message_ph:
        "Arrival time, shuttle, dietary needs, children...",
      submit: "Send request",
      submitting: "Sending...",
      success_title: "Request sent",
      success_body:
        "Thank you. We'll get back to you within 24h to confirm your stay. A WhatsApp always speeds things up.",
      error:
        "Something went wrong. Please try again or contact us on WhatsApp.",
      smoobu_note:
        "Coming soon: real-time availability via our Smoobu booking system.",
      smoobu_live_note:
        "Real-time availability on the right, synced with Booking.com and Airbnb. No double-booking risk.",
      select_room: "Choose a room",
    },
    contact: {
      overline: "Contact",
      title: "Let's talk.",
      subtitle:
        "WhatsApp, email, or call — whichever channel suits you.",
      whatsapp_cta: "Chat on WhatsApp",
      email_cta: "Send an email",
      form_title: "Or send us a message",
      name: "Name",
      email: "Email",
      message: "Message",
      send: "Send",
      sent: "Message sent, thank you.",
    },
    footer: {
      tagline: "Beachfront guesthouse · Grand-Popo, Benin",
      explore: "Explore",
      contact: "Contact",
      book_on: "Also book on",
      rights: "All rights reserved",
    },
    promo: {
      text: "Book direct and save 15% — no middleman fees.",
      short: "-15% when you book direct",
      cta: "Claim offer",
      badge_title: "Direct booking offer",
      badge_body:
        "Book directly on our site and get 15% off the listed rate. No commission, a human touch.",
    },
    activities: {
      overline: "Activities · Circuits",
      title: "Four circuits for Grand-Popo.",
      subtitle:
        "Beyond the sand, there is the mangrove, the King's Mouth, the Vodoun villages, the Fulani camps. Here are four circuits we organize with local guides.",
      source:
        "Content based on the official brochures of the Grand-Popo / Benin Tourism Office.",
      duration: "Duration",
      means: "Transport",
      stops: "On the programme",
      book: "Book this circuit (WhatsApp)",
      cta_overline: "Hesitating?",
      cta_title: "Talk it over with Jacky.",
      cta_body:
        "Jacky knows every guide personally. She helps you pick the circuit that matches your pace, your mood, your day.",
      office: "Partner guides: Lazare Dossa & Frédéric Kpokoudjo — Grand-Popo / Benin Tourism Office",
    },
    common: {
      required: "Required",
      invalid_email: "Invalid email",
      loading: "Loading...",
    },
  },
};

const LanguageContext = createContext(null);

function getTranslation(dict, path) {
  let node = dict;
  for (const part of path.split(".")) {
    node = node?.[part];
    if (node === undefined) return path;
  }
  return node;
}

export function LanguageProvider({ children }) {
  const [lang, setLang] = useState(() => storageGet("ghbb_lang") || "fr");

  const changeLang = useCallback((next) => {
    setLang(next);
    storageSet("ghbb_lang", next);
    document.documentElement.lang = next;
  }, []);

  const t = useCallback((path) => getTranslation(translations[lang], path), [lang]);

  const value = useMemo(() => ({ lang, setLang: changeLang, t }), [lang, changeLang, t]);
  return (
    <LanguageContext.Provider value={value}>
      {children}
    </LanguageContext.Provider>
  );
}

export function useI18n() {
  const ctx = useContext(LanguageContext);
  if (!ctx) throw new Error("useI18n must be used inside <LanguageProvider>");
  return ctx;
}
